import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { groupFulfillmentOrderLineItems } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { inventoryLevelAtOrigin } from "@redotech/redo-fulfillment-group-common/origin-inventory-location";
import { isWmsSotEnabledForLocation } from "@redotech/redo-fulfillment-wms-common/wms-constants";
import { RedoInventoryRpcClientContext } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ProductId } from "@redotech/redo-product-id";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { memo, useContext, useMemo, useState } from "react";
import { InventoryModal, InventoryModalRow } from "./inventory-modal";

function toSubtitle(variantTitle: string | null | undefined): string | null {
  return variantTitle && variantTitle !== "Default Title" ? variantTitle : null;
}

const toNumber = (value: bigint | null | undefined) =>
  value == null ? null : Number(value);

export const CurrentInventoryButton = memo(function CurrentInventoryButton({
  fulfillmentGroups,
}: {
  fulfillmentGroups: readonly FulfillmentGroupData[];
}) {
  const inventoryRpcClient = useRequiredContext(RedoInventoryRpcClientContext);
  const wms = useContext(TeamContext)?.settings?.outboundLabels?.wms;
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<InventoryModalRow[]>([]);

  const lineItems = useMemo(
    () =>
      groupFulfillmentOrderLineItems(
        fulfillmentGroups.flatMap((group) => group.items),
        { degroupByCustomAttributes: false },
      ),
    [fulfillmentGroups],
  );

  // Only show inventory at the locations these orders ship from.
  const origins = useMemo(
    () => fulfillmentGroups.map((group) => group.originAddress),
    [fulfillmentGroups],
  );

  const open = useHandler(async () => {
    setIsOpen(true);

    // Product identity is already known from the order; show it immediately and
    // skeleton only the inventory numbers while they load.
    setRows(
      lineItems.map((item) => ({
        key: item.redoProductId ?? item.lineItemId,
        title: item.productTitle || item.title,
        subtitle: toSubtitle(item.variantTitle),
        imageUrl: item.imageUrl,
        locationName: null,
        onHand: null,
        committed: null,
        available: null,
      })),
    );

    const detailByProduct = new Map(
      lineItems
        .filter((item) => item.redoProductId != null)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        .map((item) => [item.redoProductId as ProductId, item]),
    );
    const productIds = [...detailByProduct.keys()];

    const nextRows: InventoryModalRow[] = [];

    if (productIds.length > 0) {
      setLoading(true);
      try {
        const { items } = await inventoryRpcClient.getProductInventoryLevels({
          productIds,
        });
        for (const inventory of items) {
          if (
            !origins.some((origin) => inventoryLevelAtOrigin(origin, inventory))
          ) {
            continue;
          }
          const detail = detailByProduct.get(inventory.productId);
          nextRows.push({
            key: `${inventory.productId}:${inventory.locationId ?? "none"}`,
            title: detail?.productTitle || detail?.title || "",
            subtitle: toSubtitle(detail?.variantTitle),
            imageUrl: detail?.imageUrl,
            locationName: inventory.locationName,
            onHand: toNumber(inventory.quantity),
            committed: toNumber(inventory.committed),
            available: toNumber(inventory.available),
            bins:
              inventory.locationId &&
              isWmsSotEnabledForLocation(wms, inventory.locationId)
                ? {
                    productId: inventory.productId,
                    locationId: inventory.locationId,
                  }
                : null,
          });
        }
      } catch {
        // Degrade to whatever we can show rather than failing the popup.
      } finally {
        setLoading(false);
      }
    }

    for (const item of lineItems) {
      if (item.redoProductId != null) {
        continue;
      }
      nextRows.push({
        key: item.lineItemId,
        title: item.productTitle || item.title,
        subtitle: toSubtitle(item.variantTitle),
        imageUrl: item.imageUrl,
        locationName: null,
        onHand: null,
        committed: null,
        available: null,
      });
    }

    nextRows.sort((a, b) => {
      const aNoLocation = a.locationName == null;
      const bNoLocation = b.locationName == null;
      if (aNoLocation !== bNoLocation) {
        return aNoLocation ? 1 : -1;
      }
      const byLocation = (a.locationName ?? "").localeCompare(
        b.locationName ?? "",
      );
      return byLocation !== 0 ? byLocation : a.title.localeCompare(b.title);
    });

    setRows(nextRows);
  });

  return (
    <>
      <RedoButton
        hierarchy="secondary"
        onClick={open}
        pending={loading}
        size="sm"
        text="Inventory"
        theme="normal"
      />
      <InventoryModal
        isOpen={isOpen}
        loading={loading}
        onClose={() => setIsOpen(false)}
        rows={rows}
      />
    </>
  );
});
